import { 
  Component, 
  OnInit,
  OnDestroy,
  OnChanges,
  Input,
  SimpleChanges,
  Output,
  EventEmitter,
  Inject,
  forwardRef
} from '@angular/core';

import { 
  FormGroup,
  Validators 
} from '@angular/forms';

import { isEqual, isNil } from 'lodash-es';
import { Subject } from 'rxjs';
import { takeUntil, mergeMap, shareReplay } from 'rxjs/operators';

import { DatexFormControl, validateControlOnChange, validateFormOnControlChange } from './models/datex-form-control';
import { 
  TextBoxModel, 
  NumberBoxModel, 
  SelectBoxModel, 
  ESelectBoxType,
  DateBoxModel, 
  CheckBoxModel, 
  TextModel, 
  LabelModel, 
  ButtonModel,
  SplitButtonModel,
  SeparatorModel,
  ImageModel,
  DrawModel,
  CodeBoxModel,
  ButtonStyles,
  ValueControlModel
} from './models/control';
import { Styles, ControlContainerStyles } from './models/style';
import { FieldModel } from './models/field';
import { FieldsetModel } from './models/fieldset';
import { ToolModel } from './models/tool';
import { BaseComponent } from './components/base.component';

import { SharedModule } from './shared.module';

import { UtilsService } from './utils.service';
import { SettingsValuesService } from './settings.values.service';
import { Equipment_ShellService } from './Equipment.shell.service';
import { Equipment_OperationService } from './Equipment.operation.service';
import { Equipment_DatasourceService } from './Equipment.datasource.index';
import { Equipment_FlowService } from './Equipment.flow.index';
import { Equipment_ReportService } from './Equipment.report.index';
import { Equipment_LocalizationService } from './Equipment.localization.service';
import { Equipment_create_equipment_type_form_ComponentContextService } from './Equipment.create_equipment_type_form.component.context.service';
import { Language } from './localization.service';
import { JobStatus } from './common-interfaces'
import { ApplicationType, ComponentType } from './app-context.service';
import { CleanupLoggerService } from './cleanup.logging.service';
import { $frontendTypes} from './Equipment.frontend.types'
import { $frontendTypes as $types} from './Equipment.frontend.types' 

import { EModalSize, EToasterType, EToasterPosition } from 'wavelength-ui';

@Component({
  standalone: true,
  imports: [
    SharedModule,
  ],
  selector: 'Equipment-create_equipment_type_form',
  templateUrl: './Equipment.create_equipment_type_form.component.html'
})
export class Equipment_create_equipment_type_formComponent extends BaseComponent implements OnInit, OnDestroy {


  //#region Outputs
  @Output()
  $finish = new EventEmitter();
  outParams: { equipmentTypeId?: number, confirmed?: boolean } = { equipmentTypeId: null, confirmed: null };
  //#endregion

  //#region title
  // Make it async so that it won't cause expressionChangedAfterItHasBeenCheckedError
  // The title is often meant to be shown from the parent (shell breadcrumb for example)
  // and often it will cause an expressionChangedAfterItHasBeenCheckedError because 
  // the parent has already been checked and the child now change something on the parent 
  // in dev, CD is run twice
  $titleChange = new EventEmitter<string>(true);
  private $_title: string;
  get title(): string {
    return this.$_title;
  }
  set title(t: string) {
    this.$_title = t;
    this.$titleChange.emit(this.$_title);
  }
  //#endregion title
  //#region Variables
  //#endregion
  //#region Events
  @Output()
  outParamsChange = new EventEmitter<{ equipmentTypeId?: number, confirmed?: boolean }>();
  
  events = {
    outParamsChange: { emit: () => { this.outParamsChange.emit(this.outParams); } }
  }
  //#endregion

  formGroup: FormGroup = new FormGroup({
    equipmentTypeName: new DatexFormControl(null, { validators: [ Validators.required ], updateOn: 'blur' }),
    canhandleFullLPTasks: new DatexFormControl(null, { validators: [  ], updateOn: 'change' }),
    launchPickDropAfterPick: new DatexFormControl(null, { validators: [  ], updateOn: 'change' }),
    disableAutoConfirmOnTargetLP: new DatexFormControl(null, { validators: [  ], updateOn: 'change' }),
  });

  get valid(): boolean {
    return this.formGroup.valid;
  }
  
  toolbar = {
      confirm_button: new ToolModel(new ButtonModel('confirm_button', new ButtonStyles(['primary'], null), false, false, false, 'Confirm', '', null)
    , false),
      cancel_button: new ToolModel(new ButtonModel('cancel_button', new ButtonStyles(['secondary'], null), false, false, false, 'Cancel', '', null)
    , false)
  };

  fields = {
    equipmentTypeName: new FieldModel(new TextBoxModel(this.formGroup.controls['equipmentTypeName'] as DatexFormControl, null, false, false, '', null)
, new ControlContainerStyles(null, null), 'Equipment type', true, false),
    canhandleFullLPTasks: new FieldModel(new CheckBoxModel(this.formGroup.controls['canhandleFullLPTasks'] as DatexFormControl, null, false, false, 'Can handle full LP tasks', null)
, new ControlContainerStyles(null, null), '', false, false),
    launchPickDropAfterPick: new FieldModel(new CheckBoxModel(this.formGroup.controls['launchPickDropAfterPick'] as DatexFormControl, null, false, false, 'Launch Pick Drop after Pickup', null)
, new ControlContainerStyles(null, null), '', false, false),
    disableAutoConfirmOnTargetLP: new FieldModel(new CheckBoxModel(this.formGroup.controls['disableAutoConfirmOnTargetLP'] as DatexFormControl, null, false, false, 'Disable auto-confirm on target LP', null)
, new ControlContainerStyles(null, null), '', false, false),
  };

  fieldsets = {
    newGroup1: new FieldsetModel(
      'Equipment type',
      true,
      false,
      true,
      false,
        null
      ,
      () => {
        const $form = this;
        const $utils = this.$utils;
        return '';
      }
    ),
    capabilities: new FieldsetModel(
      'Capabilities',
      false,
      false,
      true,
      false,
        null
      ,
      () => {
        const $form = this;
        const $utils = this.$utils;
        return '';
      }
    ),
    mobileOptions: new FieldsetModel(
      'Mobile options',
      false,
      false,
      true,
      false,
        null
      ,
      () => {
        const $form = this;
        const $utils = this.$utils;
        return '';
      }
    ),
};

  //#region fields inParams
  //#endregion fields inParams

  $formGroupFieldValidationObservables = {
    equipmentTypeName: this.fields.equipmentTypeName.control.valueChanges
    ,
    canhandleFullLPTasks: this.fields.canhandleFullLPTasks.control.valueChanges
    ,
    launchPickDropAfterPick: this.fields.launchPickDropAfterPick.control.valueChanges
    ,
    disableAutoConfirmOnTargetLP: this.fields.disableAutoConfirmOnTargetLP.control.valueChanges
    ,
  }
  

  constructor(
    private $utils: UtilsService,
private $settings: SettingsValuesService,
private $shell: Equipment_ShellService,
private $datasources: Equipment_DatasourceService,
private $flows: Equipment_FlowService,
private $reports: Equipment_ReportService,
private $localization: Equipment_LocalizationService,
private $operations: Equipment_OperationService,
private $logger: CleanupLoggerService,
private $context: Equipment_create_equipment_type_form_ComponentContextService,
) { 
    super();
    this.$subscribeFormControlValueChanges();
  }

  ngOnInit(): void {
    this.$init();
  }
  

  private $unsubscribe$ = new Subject();
  ngOnDestroy(): void {
    this.$unsubscribe$.next(null);
    this.$unsubscribe$.complete();
  }

  initialized = false;

  async $init() {
    this.title = 'Create Equipment Type';
  
    const $form = this;
    const $utils = this.$utils;

    

    await this.on_init();

    this.initialized = true;
  }

  private $subscribeFormControlValueChanges() {
    this.$formGroupFieldValidationObservables
      .equipmentTypeName
      .pipe(takeUntil(this.$unsubscribe$))
      .subscribe();
    this.$formGroupFieldValidationObservables
      .canhandleFullLPTasks
      .pipe(takeUntil(this.$unsubscribe$))
      .subscribe();
    this.$formGroupFieldValidationObservables
      .launchPickDropAfterPick
      .pipe(takeUntil(this.$unsubscribe$))
      .subscribe();
    this.$formGroupFieldValidationObservables
      .disableAutoConfirmOnTargetLP
      .pipe(takeUntil(this.$unsubscribe$))
      .subscribe();
  }

  close() {
    this.$finish.emit();
  }

  openImageViewer(imageSource: string) {
    this.$shell.openImageViewerDialog(imageSource);
  }
 
  //#region private flows
  on_cancel_clicked(event = null) {
    return this.on_cancel_clickedInternal(
      this,
  this.$shell,
      this.$datasources,
      this.$flows,
      this.$reports,
      this.$settings,
      this.$operations,
      this.$utils,
      this.$context,
      // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
      // this.localization,
      event);
  }
  async on_cancel_clickedInternal(
    $form: Equipment_create_equipment_type_formComponent,
  
    $shell: Equipment_ShellService,
    $datasources: Equipment_DatasourceService,
    $flows: Equipment_FlowService,
    $reports: Equipment_ReportService,
    $settings: SettingsValuesService,
    $operations: Equipment_OperationService,
    $utils: UtilsService,
    $context: Equipment_create_equipment_type_form_ComponentContextService,
    // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
    //$l10n: Equipment_LocalizationService,
    $event: any
  ) {
  $form.close();
  }
  on_confirm_clicked(event = null) {
    return this.on_confirm_clickedInternal(
      this,
  this.$shell,
      this.$datasources,
      this.$flows,
      this.$reports,
      this.$settings,
      this.$operations,
      this.$utils,
      this.$context,
      // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
      // this.localization,
      event);
  }
  async on_confirm_clickedInternal(
    $form: Equipment_create_equipment_type_formComponent,
  
    $shell: Equipment_ShellService,
    $datasources: Equipment_DatasourceService,
    $flows: Equipment_FlowService,
    $reports: Equipment_ReportService,
    $settings: SettingsValuesService,
    $operations: Equipment_OperationService,
    $utils: UtilsService,
    $context: Equipment_create_equipment_type_form_ComponentContextService,
    // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
    //$l10n: Equipment_LocalizationService,
    $event: any
  ) {
  $form.toolbar.confirm_button.control.readOnly = true;
  $form.toolbar.cancel_button.control.readOnly = true;
  
  if(!$utils.isDefined($form.fields.equipmentTypeName.control.value))
  {
      $shell.Utilities.openErrorDialog('Cannot create Equipment Type', 'Equipment Type Name is a required field.');
      $form.toolbar.confirm_button.control.readOnly = false;
      $form.toolbar.cancel_button.control.readOnly = false;
      return;
  }
  
  try
  {
      const nextId = await $flows.Utilities.reserve_nextId_flow({ entity: 'EquipmentType' });
  
      const result = await $flows.Utilities.crud_create_flow({
          entitySet: 'EquipmentTypes',
          entity: {
              Id: nextId.nextId,
              LookupCode: $form.fields.equipmentTypeName.control.value,
              CanHandleFullLPTasksOperations: $form.fields.canhandleFullLPTasks.control.value,
              MobileLaunchPickDropAfterPickp: $form.fields.launchPickDropAfterPick.control.value,
              MobilePickingDisableAutoConfirmOnTargetLp: $form.fields.disableAutoConfirmOnTargetLP.control.value
          }
      });
  
      $form.outParams.equipmentTypeId = nextId.nextId;
      $form.outParams.confirmed = true;
  
      $form.close();
  
  } catch(error) {
  
      while ($utils.isDefined(error.error)) {
          error = error.error;
      }
      
      await $shell.Utilities.openErrorDialog('Error creating Equipment Type', error.message);
  
      $form.outParams.confirmed = false;
      $form.outParams.equipmentTypeId = null;
  
  } finally {
  
      $form.toolbar.confirm_button.control.readOnly = false;
      $form.toolbar.cancel_button.control.readOnly = false;
  
  }
  }
  on_init(event = null) {
    return this.on_initInternal(
      this,
  this.$shell,
      this.$datasources,
      this.$flows,
      this.$reports,
      this.$settings,
      this.$operations,
      this.$utils,
      this.$context,
      // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
      // this.localization,
      event);
  }
  async on_initInternal(
    $form: Equipment_create_equipment_type_formComponent,
  
    $shell: Equipment_ShellService,
    $datasources: Equipment_DatasourceService,
    $flows: Equipment_FlowService,
    $reports: Equipment_ReportService,
    $settings: SettingsValuesService,
    $operations: Equipment_OperationService,
    $utils: UtilsService,
    $context: Equipment_create_equipment_type_form_ComponentContextService,
    // Localization was developed as a POC while working on a spike 123236. This $l10n is hidden for now.
    //$l10n: Equipment_LocalizationService,
    $event: any
  ) {
  $form.fields.canhandleFullLPTasks.control.value = false;
  $form.fields.disableAutoConfirmOnTargetLP.control.value = false;
  $form.fields.launchPickDropAfterPick.control.value = false;
  
  //this doesn't seem to work?
  $form.fields.equipmentTypeName.control.focus();
  }
  //#endregion private flows
}
